import parse from 'html-react-parser';
import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react';

export type HTMLDisplayProps = {
  content: string;
  maxLines?: number;
  'data-testid'?: string;
};

export function useHTMLDisplay(props: HTMLDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!props.maxLines || !contentRef.current) {
      setShowButton(false);
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const computedStyle = window.getComputedStyle(contentRef.current);
    const lineHeight = parseFloat(computedStyle.lineHeight) || 20;
    const height = contentRef.current.scrollHeight;
    const lines = Math.ceil(height / lineHeight);

    setShowButton(lines > props.maxLines);
  }, [props.content, props.maxLines]);

  function handleToggleExpanded() {
    setIsExpanded((prev) => !prev);
  }

  const lineClampStyle: CSSProperties = useMemo(() => {
    if (!isExpanded && props.maxLines && showButton) {
      return {
        display: '-webkit-box',
        WebkitLineClamp: props.maxLines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      };
    }
    return {};
  }, [isExpanded, props.maxLines, showButton]);

  const parsedContent = useMemo(() => {
    if (!props.content) return null;
    return parse(props.content);
  }, [props.content]);

  return {
    contentRef,
    isExpanded,
    showButton,
    lineClampStyle,
    parsedContent,
    handleToggleExpanded,
  };
}
